<template>
  <div v-if="store.loading" class="about-area rn-section-gap py-10">
    <v-container>
      <v-row class="row--35 animate-pulse" align="stretch">
        <v-col cols="12" md="6" class="d-flex justify-center align-center">
          <v-skeleton-loader
            type="image"
            height="400"
            width="100%"
            :color="style.ThemeGeneral?.TextoCss?.Color ? style.ThemeGeneral.TextoCss.Color + '10' : 'rgba(0,0,0,0.05)'"
            class="rounded-xl"
          ></v-skeleton-loader>
        </v-col>
        <v-col cols="12" md="6" class="d-flex flex-column justify-center">
          <v-skeleton-loader
            type="heading, paragraph"
            :color="style.ThemeGeneral?.TextoCss?.Color ? style.ThemeGeneral.TextoCss.Color + '10' : 'rgba(0,0,0,0.05)'"
            class="rounded-xl"
          ></v-skeleton-loader>
        </v-col>
      </v-row>
    </v-container>
  </div>

  <div class="about-modern-area rn-section-gap" id="about" v-else-if="NOTARIA && NOTARIA.Theme && NOTARIA.Theme.Home.PN.Mostrar">
    <v-container>
      <div class="about-modern-card glass-premium rounded-3xl pa-8 pa-md-12">
        <!-- SVG Quote Watermark in Background -->
        <div class="quote-watermark">
          <svg viewBox="0 0 100 100" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
            <path d="M30 40c0-10 5-15 15-15v10c-5 0-7 2-7 5h7v15H30V40zm25 0c0-10 5-15 15-15v10c-5 0-7 2-7 5h7v15H55V40z" />
          </svg>
        </div>

        <v-row :class="['row-gap-30', { 'flex-row-reverse': NOTARIA.Theme.Home.PN.ImageLeft === false }]" align="center">
          <!-- Columna Imagen con efectos de profundidad y marco flotante -->
          <v-col cols="12" sm="5" class="d-flex justify-center position-relative">
            <div class="modern-image-wrapper">
              <div class="gradient-glow-aura"></div>
              <div class="image-inner-container rounded-2xl">
                <v-img 
                  :src="imagenIDtoSRC(NOTARIA.Theme.Home.PN.Imagen)" 
                  :alt="'Fotografía de ' + NOTARIA.Nombre + ' - Notario'" 
                  class="modern-notary-img"
                  cover
                />
              </div>
            </div>
          </v-col>

          <!-- Columna Contenido con diseño y tipografía de vanguardia -->
          <v-col cols="12" sm="7" class="d-flex flex-column justify-center">
            <div class="modern-content-wrapper" :class="{ 'pl-sm-6': NOTARIA.Theme.Home.PN.ImageLeft !== false, 'pr-sm-6': NOTARIA.Theme.Home.PN.ImageLeft === false }" :style="descripcionStyle">
              <h2 class="modern-heading-title mb-6" :style="tituloStyle" v-html="NOTARIA.Theme.Home.PN.Titulo.Texto">
              </h2>
              
              <v-divider class="short-accent-divider mb-6" :style="{ '--divider-color': style.ThemeHomePalabrasNotario?.Titulo?.css?.Color || '#3949ab' }"></v-divider>

              <div 
                v-if="NOTARIA.Theme.Home.PN.Descripcion.Texto" 
                v-html="NOTARIA.Theme.Home.PN.Descripcion.Texto" 
                class="modern-rich-text-content"
              ></div>
            </div>
          </v-col>
        </v-row>
      </div>
    </v-container>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useStore } from '../../store/index'
import { useUtils } from '#imports'
import { useStyles } from '../../store/styles'

const { imagenIDtoSRC } = useUtils()
const store = useStore()
const { NOTARIA } = storeToRefs(store)
const style = useStyles()

const tituloStyle = computed(() => ({
  color: style.ThemeHomePalabrasNotario?.Titulo?.css?.Color ?? '',
  textShadow: style.ThemeHomePalabrasNotario?.Titulo?.css?.BackgroundColor  ? `4px 2px 6px ${style.ThemeHomePalabrasNotario.Titulo.css.BackgroundColor}` : '',
  fontFamily: style.ThemeHomePalabrasNotario?.Titulo?.css?.Font ?? '',
  fontSize: style.ThemeHomePalabrasNotario?.Titulo?.css?.Size ?? '',
  fontWeight: style.ThemeHomePalabrasNotario?.Titulo?.css?.Weight ?? '',
  textAlign: style.ThemeHomePalabrasNotario?.Titulo?.css?.Align ?? ''
}))

const descripcionStyle = computed(() => ({
  color: style.ThemeHomePalabrasNotario?.Descripcion?.css?.Color ?? '',
  textShadow: style.ThemeHomePalabrasNotario?.Descripcion?.css?.BackgroundColor ? `4px 2px 6px ${style.ThemeHomePalabrasNotario.Descripcion.css.BackgroundColor}` : '',
  fontFamily: style.ThemeHomePalabrasNotario?.Descripcion?.css?.Font ?? '',
  fontSize: style.ThemeHomePalabrasNotario?.Descripcion?.css?.Size ?? '',
  fontWeight: style.ThemeHomePalabrasNotario?.Descripcion?.css?.Weight ?? '',
  textAlign: style.ThemeHomePalabrasNotario?.Descripcion?.css?.Align ?? ''
}))
</script>

<style scoped lang="scss">
.about-modern-area {
  position: relative;
  overflow: hidden;
  padding: 80px 0;
}

.about-modern-card {
  position: relative;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.03);
  transition: all 0.5s ease;
  overflow: hidden;

  &.glass-premium {
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
  }

  &:hover {
    box-shadow: 0 25px 60px rgba(0, 0, 0, 0.06);
    border-color: rgba(0, 0, 0, 0.08);

    .gradient-glow-aura {
      transform: scale(1.1) rotate(5deg);
      opacity: 0.15;
    }

    .modern-notary-img {
      transform: scale(1.03);
    }
  }
}

.quote-watermark {
  position: absolute;
  bottom: -20px;
  right: -20px;
  width: 250px;
  height: 250px;
  color: rgba(57, 73, 171, 0.04);
  pointer-events: none;
  z-index: 0;
  transform: rotate(-10deg);
}

.row-gap-30 {
  row-gap: 30px;
}

.modern-image-wrapper {
  position: relative;
  width: 100%;
  max-width: 380px;
  aspect-ratio: 4/5;
  z-index: 1;
}

.gradient-glow-aura {
  position: absolute;
  inset: -15px;
  background: linear-gradient(135deg, #3949ab, #1a237e);
  filter: blur(25px);
  opacity: 0.08;
  border-radius: 40px;
  transition: all 0.6s cubic-bezier(0.165, 0.84, 0.44, 1);
  z-index: -1;
}

.image-inner-container {
  width: 100%;
  height: 100%;
  overflow: hidden;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.12);
  border: 4px solid #ffffff;
  background: #f7f9fc;
}

.modern-notary-img {
  width: 100%;
  height: 100%;
  transition: transform 0.8s cubic-bezier(0.165, 0.84, 0.44, 1);
}

.modern-content-wrapper {
  position: relative;
  z-index: 1;
}

.color-accent-indigo {
  color: #3949ab;
  font-size: 0.85rem;
  letter-spacing: 2px;
}

.modern-heading-title {
  font-weight: 800;
  line-height: 1.25;
  letter-spacing: -0.5px;
}

.short-accent-divider {
  width: 60px;
  border-width: 2px !important;
  border-color: var(--divider-color, #3949ab) !important;
  opacity: 0.8;
}

.modern-rich-text-content {
  color: inherit;
  font-family: inherit;
  text-align: inherit;
  font-size: inherit;
  line-height: 1.7;
  
  :deep(p), :deep(ul), :deep(ol), :deep(li), :deep(span) {
    color: inherit;
    font-family: inherit;
    text-align: inherit;
    font-size: inherit;
    font-weight: inherit;
  }

  :deep(strong), :deep(b) {
    font-weight: bold !important;
  }
}

@media (max-width: 960px) {
  .about-modern-card {
    padding: 30px 20px !important;
  }
  .modern-image-wrapper {
    max-width: 280px;
  }
  .quote-watermark {
    width: 150px;
    height: 150px;
  }
}
</style>
